import { REGEX_COMMENT_LINE, REGEX_COMMENT_MULTILINE } from '@app/const/consts';
import { DocumentFieldDependency, IDocumentDetailsField, IDocumentValuesFields } from '@app/store/modules/documents/types';
import { IDictionary } from '@app/store/types/commonTypes';
import { DataType, IDocumentTypeField, IDocumentTypeFields, ITableField } from '@app/store/modules/metadata/types';
import { DateFormat, FieldType } from '@app/const/consts';
import { convertDateBetweenZones, getUserTimeZone } from '@infologistics/frontend-libraries';
import { DateTime } from 'luxon';

//prettier-ignore
export const DBG = Array(20).fill(false);

DBG[0] = false;
DBG[1] = false;
DBG[2] = false;
DBG[3] = false;
DBG[4] = false;
DBG[5] = false;
DBG[6] = false;

// Удаляет однострочные и многострочные комментарии, чтобы они не влияли на regex-разбор скрипта.
function removeComments(code: string) {
  const scriptFinal = code.replace(REGEX_COMMENT_LINE, '');
  return scriptFinal.replace(REGEX_COMMENT_MULTILINE, '');
}

// Удаляет console.log из скрипта перед статическим анализом зависимостей.
function removeConsoleLog(code: string) {
  return code.replace(/console\.log\s*\([\s\S]*?\);?/g, '');
}

// Загружает локально сохраненный debug-скрипт для конкретного типа документа.
export const loadScript = (docType: string) => {
  const lsItemName = `df_scripts__${docType}`;

  const lsDfScripts = localStorage.getItem(lsItemName);
  if (lsDfScripts) {
    return lsDfScripts;
  }

  return '';
};

// const evalScript = (data: string) => {
//   return eval(`"use strict"; ${data}`);
// };
// const funcScrict = (data: string) => {
//   return Function(`"use strict"; return ${data}`);
// };

//--------------------------------------------------------------------------
//-- REGEX
//--------------------------------------------------------------------------
const REGEX_COMMON_FIELD_NAME = /(?<!\.)(\b\w+\b)(?=\.value(?!\s*=(?!=)))/g;
const REGEX_COMMON_FIELD = /(?<!\.)(\b\w+\b)\.value(?!\s*=(?!=))/g;
const REGEX_COMMON_FIELD_KEY_NAME = /(?<!\.)(\b\w+\b)(?=\.key(?!\s*=(?!=)))/g;
const REGEX_COMMON_FIELD_KEY = /(?<!\.)(\b\w+\b)\.key(?!\s*=(?!=))/g;
const REGEX_COMMON_FIELD_NAME_LEFTHAND = /(\b\w+\b)(?=\.value(\s+|)=(?!=))/g;
const REGEX_COMMON_FIELD_LEFTHAND = /(\b\w+\b).value(\s+|)=(?!=)/g;
const REGEX_COMMON_FIELD_KEY_NAME_LEFTHAND = /(\b\w+\b)(?=\.key(\s+|)=(?!=))/g;
const REGEX_COMMON_FIELD_KEY_LEFTHAND = /(\b\w+\b).key(\s+|)=(?!=)/g;

const REGEX_TABLE_LENGTH = /table.length/g;

const REGEX_TABLE_I_FIELD = /(table\[\w+\]\.\b\w+\b)\.value(?!\s*=(?!=))/g;
const REGEX_TABLE_I_FIELD_NAME =
  /(?<=table)(\[\w+\]\.\b\w+\b)(?=\.value(?!\s*=(?!=)))/g;
const REGEX_TABLE_I_FIELD_KEY = /(table\[\w+\]\.\b\w+\b)\.key(?!\s*=(?!=))/g;
const REGEX_TABLE_I_FIELD_NAME_KEY =
  /(?<=table)(\[\w+\]\.\b\w+\b)(?=\.key(?!\s*=(?!=)))/g;
const REGEX_TABLE_I_FIELD_LEFTHAND = /table\[\w+\]\.(\w+)\.value(\s+|)=(?!=)/g;
const REGEX_TABLE_I_FIELD_NAME_LEFTHAND = /(?<=table)(\[\w+\]\.\w+)(?=\.value(\s+|)=(?!=))/g;
const REGEX_TABLE_I_FIELD_KEY_LEFTHAND = /table\[\w+\]\.(\w+)\.key(\s+|)=(?!=)/g;
const REGEX_TABLE_I_FIELD_NAME_KEY_LEFTHAND = /(?<=table)(\[\w+\]\.\w+)(?=\.key(\s+|)=(?!=))/g;

const REGEX_COMMON_FORMATTED_FIELD_NAME_LEFTHAND = /(?<!\$)(\b\w+\b)(?=\.value(\s+|)=(?!=))/g;
// const REGEX_COMMON_FORMATTED_FIELD_NAME2 = new RegExp(String.raw`(?<!\$)${REGEX_COMMON_FIELD_NAME}`, "g");
// console.log({REGEX_COMMON_FORMATTED_FIELD_NAME,REGEX_COMMON_FORMATTED_FIELD_NAME2, REGEX_COMMON_FIELD_NAME})
const REGEX_COMMON_FORMATTED_FIELD_LEFTHAND = /(?<!\$)(\b\w+\b).value(\s+|)=(?!=)/g;
const REGEX_COMMON_FORMATTED_FIELD_NAME_KEY_LEFTHAND = /(?<!\$)(\b\w+\b)(?=\.key(\s+|)=(?!=))/g;
const REGEX_COMMON_FORMATTED_FIELD_KEY_LEFTHAND = /(?<!\$)(\b\w+\b).key(\s+|)=(?!=)/g;

// const REGEX_FUNC_NAME = /(?<=function).+(\b\w+\b)/g;
const REGEX_FUNC_START = /(?<=function)\s+\w+\s*\([^)]*\)\s*{/g;
// const REGEX_REPLACE_SPACE = /\s/g;

// const REGEX_TABLE_ONCHANGE_NAME = /(\b\w+\b)(?=\.onChange)/g;
// const REGEX_TABLE_ONCHANGE_OTHER = /(?<=.onChange)(.*)/g;
// const REGEX_TABLE_ONCHANGE_LINE = /(table\.\b\w+\b)\.onChange/g;

const REGEX_ONCHANGE_NAME = /(\b\w+\b)(?=\.onChange)/g;
const REGEX_ONCHANGE_NAME_DEPENDENCY = /((table\.\b\w+\b)|(\b\w+\b))(?=\.onChange)/g;
const REGEX_ONCHANGE_OTHER = /(?<=.onChange)(.*)/g;
const REGEX_SPACE_EQ_SEMICOLON = /\s|=|;/g;
const REGEX_ONCHANGE_LINE = /(?=.*.onChange).+/g;
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const getClientScriptFunctionName = (functionStart: string): string =>
  functionStart.match(/\b\w+\b/)?.[0] ?? '';

export interface IScriptParams {
  fields: IDocumentValuesFields;
  details: IDocumentDetailsField[];
}

export interface IScriptUpdNames {
  fields: string[];
  details: string[];
}

interface IScriptUpdObj {
  updParams: IScriptParams;
  updNames: IScriptUpdNames;
}

// Проверяет, что значение похоже на объект справочника с ключом.
const isDictionaryLike = (fieldValue: unknown): fieldValue is Partial<IDictionary> => {
  return typeof fieldValue === 'object' && !!fieldValue && 'key' in fieldValue;
};

// Создает безопасное значение справочника с пустыми строками по умолчанию.
const createDictionaryValue = (key = '', value = ''): IDictionary => ({
  key,
  value,
});

const createDictionaryArrayValue = (value: unknown): IDictionary[] => {
  if (!Array.isArray(value)) return [];

  return value.reduce<IDictionary[]>((acc, item) => {
    if (isDictionaryLike(item)) {
      return [
        ...acc,
        createDictionaryValue(String(item.key ?? ''), String(item.value ?? item.key ?? '')),
      ];
    }

    if (typeof item === 'string') {
      return [...acc, createDictionaryValue(item, item)];
    }

    return acc;
  }, []);
};

// Приводит значение справочника к стабильному виду { key, value } для чтения и записи в скрипте.
const normalizeDictionaryValue = (fieldValue: unknown): IDictionary => {
  if (isDictionaryLike(fieldValue)) {
    return createDictionaryValue(String(fieldValue.key ?? ''), String(fieldValue.value ?? ''));
  }

  if (typeof fieldValue === 'string') {
    return createDictionaryValue(fieldValue, '');
  }

  return createDictionaryValue();
};

// Достает ключ поля из выражения вида [i].fieldName.
const getTableFieldKey = (tableFieldName: string) => tableFieldName.split('.')[1];

type ScriptDateFieldType = Extract<DataType, 'DATE' | 'DATETIME'>;

const CLIENT_SCRIPT_DATE_FIELD_TYPE = '__dfClientScriptDateFieldType';

// Проверяет, относится ли тип поля к датам, для которых нужен TZ-адаптер скрипта.
const isDateFieldType = (type?: DataType): type is ScriptDateFieldType =>
  type === FieldType.DATE || type === FieldType.DATETIME;

// Проверяет, что значение является валидным Date, а не Invalid Date.
const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

// Возвращает формат строкового вывода для даты с учетом исходного типа поля.
const getScriptDateFormat = (type: ScriptDateFieldType) =>
  type === FieldType.DATE
    ? DateFormat.DATE_FULL_YEAR_DATEPICKER
    : DateFormat.DATETIME_FULL_YEAR_DATEPICKER;

// Читает скрытую метку исходного типа DATE/DATETIME с Date-значения из скрипта.
const getScriptDateFieldType = (value: Date): ScriptDateFieldType | undefined =>
  (value as Date & { [CLIENT_SCRIPT_DATE_FIELD_TYPE]?: ScriptDateFieldType })[
    CLIENT_SCRIPT_DATE_FIELD_TYPE
  ];

// Помечает Date исходным типом поля, чтобы при записи в STRING выбрать корректный формат.
const setScriptDateFieldType = (value: Date, type: ScriptDateFieldType): Date => {
  Object.defineProperty(value, CLIENT_SCRIPT_DATE_FIELD_TYPE, {
    configurable: true,
    enumerable: false,
    value: type,
  });

  return value;
};

// Преобразует пользовательское значение даты из Date, timestamp или строки в Date.
const parseScriptDateValue = (
  value: unknown,
  type: ScriptDateFieldType,
  timezone: string
): Date | null => {
  if (isValidDate(value)) return new Date(value.getTime());

  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value);
    return isValidDate(date) ? date : null;
  }

  if (typeof value !== 'string' || !value.trim()) return null;

  const formats =
    type === FieldType.DATE
      ? [DateFormat.DATE_FULL_YEAR_DATEPICKER, DateFormat.DATE_FULL_YEAR]
      : [
          DateFormat.DATETIME_FULL_YEAR_DATEPICKER,
          DateFormat.DATETIME_FULL_YEAR,
          DateFormat.DATE_FULL_YEAR_DATEPICKER,
          DateFormat.DATE_FULL_YEAR,
        ];

  for (const format of formats) {
    const datetime = DateTime.fromFormat(value, format, { zone: timezone });
    if (datetime.isValid) return datetime.toJSDate();
  }

  const date = new Date(value);
  return isValidDate(date) ? date : null;
};

// Переносит дату между зонами без перекладывания этой логики в пользовательский скрипт.
const convertScriptDateValue = (
  value: unknown,
  type: ScriptDateFieldType,
  sourceTimezone: string,
  targetTimezone: string
): Date | null => {
  const date = parseScriptDateValue(value, type, sourceTimezone);
  if (!date) return null;

  return convertDateBetweenZones(date, sourceTimezone, targetTimezone);
};

// Форматирует Date в строку DATE или DATETIME в переданной таймзоне.
const formatScriptDateValue = (
  value: Date,
  type: ScriptDateFieldType,
  timezone: string
): string =>
  DateTime.fromJSDate(value, { zone: timezone }).toFormat(getScriptDateFormat(type));

// Готовит DATE/DATETIME к чтению в скрипте: UTC-значение формы переводит в TZ пользователя.
const convertDateValueToClientScript = (
  value: unknown,
  type: ScriptDateFieldType,
  userTimeZone: string
): unknown => {
  if (value === null || value === undefined || value === '') return value;

  const convertedDate = convertScriptDateValue(value, type, 'UTC', userTimeZone);
  return convertedDate ? setScriptDateFieldType(convertedDate, type) : value;
};

// Готовит результат записи в обычное DATE/DATETIME поле: значение из TZ пользователя переводит в UTC.
const convertCommonDateValueFromClientScript = (
  value: unknown,
  type: ScriptDateFieldType,
  userTimeZone: string
): Date | null => {
  if (value === null || value === undefined || value === '') return null;

  return convertScriptDateValue(value, type, userTimeZone, 'UTC');
};

// Готовит результат записи в табличное DATE/DATETIME поле в строковом формате таблицы.
const convertTableDateValueFromClientScript = (
  value: unknown,
  type: ScriptDateFieldType,
  userTimeZone: string
): string => {
  const convertedDate = convertCommonDateValueFromClientScript(value, type, userTimeZone);
  return convertedDate ? formatScriptDateValue(convertedDate, type, 'UTC') : '';
};

// При записи Date в STRING возвращает строку в пользовательской TZ и формате исходного типа даты.
const convertDateValueForStringField = (value: unknown, userTimeZone: string): unknown => {
  if (!isValidDate(value)) return value;

  return formatScriptDateValue(
    value,
    getScriptDateFieldType(value) ?? FieldType.DATETIME,
    userTimeZone
  );
};

const normalizeStringClientScriptValue = (value: unknown, userTimeZone: string): string => {
  const normalizedValue = convertDateValueForStringField(value, userTimeZone);

  return normalizedValue === null || normalizedValue === undefined ? '' : String(normalizedValue);
};

const getClientScriptValueExpression = (
  valueExpression: string,
  type?: DataType,
  isArray = false
): string => {
  if (type === FieldType.DICTIONARY) {
    return isArray
      ? `(${valueExpression} ?? [])`
      : `(${valueExpression}?.value ?? '')`;
  }

  if (isDateFieldType(type)) {
    return `(${valueExpression})`;
  }

  if (type === FieldType.NUMBER) {
    return `(Number(${valueExpression}) || 0)`;
  }

  if (type === FieldType.STRING) {
    return `(${valueExpression} == null ? '' : String(${valueExpression}))`;
  }

  if (type === FieldType.BOOLEAN) {
    return `(typeof ${valueExpression} === 'boolean' ? ${valueExpression} : Boolean(${valueExpression}))`;
  }

  if (type === FieldType.CONTRACTOR || type === FieldType.FILE) {
    return `(${valueExpression} ?? null)`;
  }

  return `(${valueExpression} ?? '')`;
};

const createClientScriptFieldDefaultValue = (field: IDocumentTypeField): IDocumentValuesFields[string] => {
  if (field.type === FieldType.BOOLEAN) return false;

  if (field.type === FieldType.DATE || field.type === FieldType.DATETIME) return null;

  if (field.type === FieldType.DICTIONARY) {
    return field.isArray ? [] : createDictionaryValue();
  }

  if (field.type === FieldType.FILE) {
    return field.isArray ? [] : null;
  }

  if (field.type === FieldType.CONTRACTOR) return null;

  return '';
};

// Создает копию входных параметров скрипта с датами, переведенными в таймзону пользователя.
const createClientScriptParams = (
  params: IScriptParams,
  fieldsTypes: IDocumentTypeFields,
  tableFieldsTypes: ITableField[],
  userTimeZone: string
): IScriptParams => {
  const fields = { ...params.fields };

  Object.keys(fieldsTypes).forEach((fieldKey) => {
    const field = fieldsTypes[fieldKey];
    const fieldType = field?.type;

    if (!(fieldKey in fields)) {
      fields[fieldKey] = createClientScriptFieldDefaultValue(field);
    }

    if (fieldType === FieldType.DICTIONARY) {
      fields[fieldKey] = field.isArray
        ? createDictionaryArrayValue(fields[fieldKey])
        : normalizeDictionaryValue(fields[fieldKey]);
    }

    if (isDateFieldType(fieldType)) {
      if (!(fieldKey in fields)) return;

      fields[fieldKey] = convertDateValueToClientScript(
        fields[fieldKey],
        fieldType,
        userTimeZone
      ) as IDocumentValuesFields[string];
    }
  });

  const details = (params.details ?? []).map((detail) => {
    const nextDetail = { ...detail };

    tableFieldsTypes.forEach((field) => {
      if (field.type === FieldType.DICTIONARY) {
        nextDetail[field.key] = normalizeDictionaryValue(nextDetail[field.key]);
      }

      if (isDateFieldType(field.type)) {
        if (!(field.key in nextDetail)) return;

        nextDetail[field.key] = convertDateValueToClientScript(
          nextDetail[field.key],
          field.type,
          userTimeZone
        ) as IDocumentDetailsField[string];
      }
    });

    return nextDetail;
  });

  return { fields, details };
};

// Нормализует результат выполнения скрипта обратно к форматам, ожидаемым формами документа.
const prepareClientScriptResult = (
  params: IScriptParams,
  fieldsTypes: IDocumentTypeFields,
  tableFieldsTypes: ITableField[],
  userTimeZone: string
): IScriptParams => {
  const fields = { ...params.fields };

  Object.keys(fieldsTypes).forEach((fieldKey) => {
    if (!(fieldKey in fields)) return;

    const field = fieldsTypes[fieldKey];
    const fieldType = field?.type;

    if (fieldType === FieldType.DICTIONARY && field.isArray) {
      fields[fieldKey] = createDictionaryArrayValue(fields[fieldKey]);
    }

    if (isDateFieldType(fieldType)) {
      fields[fieldKey] = convertCommonDateValueFromClientScript(
        fields[fieldKey],
        fieldType,
        userTimeZone
      );
    }

    if (fieldType === FieldType.STRING) {
      fields[fieldKey] = normalizeStringClientScriptValue(fields[fieldKey], userTimeZone);
    }
  });

  const details = (params.details ?? []).map((detail) => {
    const nextDetail = { ...detail };

    tableFieldsTypes.forEach((field) => {
      if (!(field.key in nextDetail)) return;

      if (isDateFieldType(field.type)) {
        nextDetail[field.key] = convertTableDateValueFromClientScript(
          nextDetail[field.key],
          field.type,
          userTimeZone
        );
      }

      if (field.type === FieldType.STRING) {
        nextDetail[field.key] = normalizeStringClientScriptValue(
          nextDetail[field.key],
          userTimeZone
        );
      }
    });

    return nextDetail;
  });

  return { fields, details };
};

// Приводит значение к виду, пригодному для сравнения с учетом особенностей даты и справочника.
const getComparableClientScriptValue = (
  value: unknown,
  type?: DataType,
  isArray = false
): unknown => {
  if (isDateFieldType(type)) {
    return parseScriptDateValue(value, type, getUserTimeZone())?.getTime() ?? null;
  }

  if (type === FieldType.DICTIONARY) {
    if (isArray) {
      return createDictionaryArrayValue(value)
        .map(({ key, value }) => `${key}:${value}`)
        .join('|');
    }

    const normalizedValue = normalizeDictionaryValue(value);
    return `${normalizedValue.key}:${normalizedValue.value}`;
  }

  return value;
};

// Сравнивает старое и новое значение поля так, чтобы Date-экземпляры не вызывали лишний onChange.
const isClientScriptValueChanged = (
  newValue: unknown,
  oldValue: unknown,
  type?: DataType,
  isArray = false
): boolean =>
  getComparableClientScriptValue(newValue, type, isArray) !==
  getComparableClientScriptValue(oldValue, type, isArray);

// Находит поля, в которые клиентский скрипт пишет значения, чтобы сделать их readonly в форме.
export const getClientScriptReadonlyFields = (script: string) => {
  const updNames: IScriptUpdNames = {
    fields: [],
    details: [],
  };

  let scriptFinal = removeComments(script);
  scriptFinal = removeConsoleLog(scriptFinal);
  //--------------------------------------------------------------------------

  const funcStart = scriptFinal.match(REGEX_FUNC_START);
  if (funcStart) {
    for (let i = 0; i < funcStart.length; i++) {
      const idxNameStart = scriptFinal.indexOf(funcStart[i]);
      const idxNameEnd = idxNameStart + funcStart[i].length;

      let bracketNum = 1;
      let j = idxNameEnd;
      let idxEnd = idxNameEnd;
      while (bracketNum !== 0) {
        switch (scriptFinal[j]) {
          case '{':
            bracketNum++;
            break;
          case '}':
            if (bracketNum === 1) {
              idxEnd = j;
            }
            bracketNum--;
            break;
          default:
            break;
        }
        if (j === scriptFinal.length - 1) {
          break;
        }
        j++;
      }

      //--------------------------------------------------------------------------
      let funcFinal = scriptFinal.slice(idxNameEnd, idxEnd);

      const tableFieldsLefthand = [
        ...(funcFinal.match(REGEX_TABLE_I_FIELD_LEFTHAND) ?? []),
        ...(funcFinal.match(REGEX_TABLE_I_FIELD_KEY_LEFTHAND) ?? []),
      ];

      if (tableFieldsLefthand.length) {
        for (let n = 0; n < tableFieldsLefthand.length; n++) {
          const name = tableFieldsLefthand[n].match(/\.(\w+)\./);
          if (name) {
            updNames.details.push(name[1]);
          }
        }

        for (let k = 0; k < tableFieldsLefthand.length; k++) {
          while (funcFinal.indexOf(tableFieldsLefthand[k]) !== -1) {
            funcFinal = funcFinal.replace(tableFieldsLefthand[k], '');
          }
        }
      }

      const commonFieldsLeftHand = [
        ...(funcFinal.match(REGEX_COMMON_FIELD_LEFTHAND) ?? []),
        ...(funcFinal.match(REGEX_COMMON_FIELD_KEY_LEFTHAND) ?? []),
      ];

      if (commonFieldsLeftHand.length) {
        for (let n = 0; n < commonFieldsLeftHand.length; n++) {
          const name = commonFieldsLeftHand[n].match(/(\b\w+\b)/);
          if (name) {
            updNames.fields.push(name[0]);
          }
        }
      }
    }
  }

  return updNames;
};

// Строит карту зависимостей: какие поля должны пересчитываться при изменении источников onChange.
export const getClientScriptDependencies = (script: string) => {
  const funcDependencies: DocumentFieldDependency = {};
  const fieldsDependencies: DocumentFieldDependency = {};

  let scriptFinal = removeComments(script);
  scriptFinal = removeConsoleLog(scriptFinal);
  //--------------------------------------------------------------------------

  const funcStart = scriptFinal.match(REGEX_FUNC_START);
  if (funcStart) {
    for (let i = 0; i < funcStart.length; i++) {
      const funcName = getClientScriptFunctionName(funcStart[i]);

      const idxNameStart = scriptFinal.indexOf(funcStart[i]);
      const idxNameEnd = idxNameStart + funcStart[i].length;

      let bracketNum = 1;
      let j = idxNameEnd;
      let idxEnd = idxNameEnd;
      while (bracketNum !== 0) {
        switch (scriptFinal[j]) {
          case '{':
            bracketNum++;
            break;
          case '}':
            if (bracketNum === 1) {
              idxEnd = j;
            }
            bracketNum--;
            break;
          default:
            break;
        }
        if (j === scriptFinal.length - 1) {
          break;
        }
        j++;
      }

      //--------------------------------------------------------------------------
      let funcFinal = scriptFinal.slice(idxNameEnd, idxEnd);
      //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      // --------------------------------------------------------------------------
      // replace table fields
      // --------------------------------------------------------------------------
      const tableLefthandConfigs = [
        {
          matches: funcFinal.match(REGEX_TABLE_I_FIELD_LEFTHAND),
          names: funcFinal.match(REGEX_TABLE_I_FIELD_NAME_LEFTHAND),
        },
        {
          matches: funcFinal.match(REGEX_TABLE_I_FIELD_KEY_LEFTHAND),
          names: funcFinal.match(REGEX_TABLE_I_FIELD_NAME_KEY_LEFTHAND),
        },
      ];

      tableLefthandConfigs.forEach(({ matches, names }) => {
        if (matches && names) {
          const tableFieldsNamesLefthandReplaced = names.map((val) => 'tbl$' + val.split('.')[1]);

          for (let k = 0; k < matches.length; k++) {
            while (funcFinal.indexOf(matches[k]) !== -1) {
              funcFinal = funcFinal.replace(matches[k], `${tableFieldsNamesLefthandReplaced[k]}.value=`);
            }
          }

          funcDependencies[funcName] = [...(funcDependencies[funcName] ?? []), ...tableFieldsNamesLefthandReplaced];
        }
      });

      const tableFields = funcFinal.match(REGEX_TABLE_I_FIELD);
      const tableFieldsNames = funcFinal.match(REGEX_TABLE_I_FIELD_NAME);
      if (tableFields && tableFieldsNames) {
        const tableFieldsNamesReplaced = [...tableFieldsNames.map((val) => 'tbl$' + val.split('.')[1])];
        for (let k = 0; k < tableFields.length; k++) {
          while (funcFinal.indexOf(tableFields[k]) !== -1) {
            // замена, чтобы корректно находить common поля
            funcFinal = funcFinal.replace(tableFields[k], `${tableFieldsNamesReplaced[k]}$value`);
          }
        }
      }
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

      // --------------------------------------------------------------------------
      // replace common fields
      // --------------------------------------------------------------------------
      const commonFieldsLeftHand = funcFinal.match(REGEX_COMMON_FORMATTED_FIELD_LEFTHAND);
      const commonFieldsNamesLeftHand = funcFinal.match(REGEX_COMMON_FORMATTED_FIELD_NAME_LEFTHAND);
      if (commonFieldsLeftHand && commonFieldsNamesLeftHand) {
        funcDependencies[funcName] = [...(funcDependencies[funcName] ? funcDependencies[funcName] : []), ...commonFieldsNamesLeftHand];
      }

      const commonFieldsKeyLeftHand = funcFinal.match(REGEX_COMMON_FORMATTED_FIELD_KEY_LEFTHAND);
      const commonFieldsNamesKeyLeftHand = funcFinal.match(REGEX_COMMON_FORMATTED_FIELD_NAME_KEY_LEFTHAND);
      if (commonFieldsKeyLeftHand && commonFieldsNamesKeyLeftHand) {
        funcDependencies[funcName] = [...(funcDependencies[funcName] ? funcDependencies[funcName] : []), ...commonFieldsNamesKeyLeftHand];
      }

      funcDependencies[funcName] = [...new Set(funcDependencies[funcName])];
    }
  }

  const onChangeLines = scriptFinal.match(REGEX_ONCHANGE_LINE);
  if (onChangeLines) {
    for (let i = 0; i < onChangeLines.length; i++) {
      const name = onChangeLines[i].match(REGEX_ONCHANGE_NAME_DEPENDENCY);
      const other = onChangeLines[i].match(REGEX_ONCHANGE_OTHER);
      if (name && other) {
        const func = other[0].replace(REGEX_SPACE_EQ_SEMICOLON, '');

        const newName = name[0].replace('table.', 'tbl$');

        if (func in funcDependencies && funcDependencies[func].length) {
          funcDependencies[func].forEach((val) => {
            fieldsDependencies[val] = fieldsDependencies[val] ? [...fieldsDependencies[val], newName] : [newName];
          });
        }
      }
    }
  }
  return fieldsDependencies;
};

// Выполняет пользовательский скрипт над копией полей и возвращает обновленные значения и имена измененных полей.
export const applyClientScript = (
  script: string,
  newParams: IScriptParams,
  oldParams: IScriptParams,
  fieldsTypes: IDocumentTypeFields,
  tableFieldsTypes?: ITableField[],
) => {
  const tableFields = tableFieldsTypes ?? [];
  const userTimeZone = getUserTimeZone();
  const scriptNewParams = createClientScriptParams(newParams, fieldsTypes, tableFields, userTimeZone);
  const scriptOldParams = createClientScriptParams(oldParams, fieldsTypes, tableFields, userTimeZone);
  const updObj: IScriptUpdObj = {
    updParams: scriptNewParams,
    updNames: {
      fields: [],
      details: [],
    },
  };

  const funcObj = {};
  const tableFieldsTypesMap = new Map(tableFields.map((field) => [field.key, field.type]));

  //--------------------------------------------------------------------------
  // remove comments
  //--------------------------------------------------------------------------
  const scriptFinal = removeComments(script);
  // scriptFinal = removeConsoleLog(scriptFinal);
  //--------------------------------------------------------------------------

  const funcStart = scriptFinal.match(REGEX_FUNC_START);
  if (funcStart) {
    for (let i = 0; i < funcStart.length; i++) {
      const funcName = getClientScriptFunctionName(funcStart[i]);

      const idxNameStart = scriptFinal.indexOf(funcStart[i]);
      const idxNameEnd = idxNameStart + funcStart[i].length;

      let bracketNum = 1;
      let j = idxNameEnd;
      let idxEnd = idxNameEnd;
      while (bracketNum !== 0) {
        switch (scriptFinal[j]) {
          case '{':
            bracketNum++;
            break;
          case '}':
            if (bracketNum === 1) {
              idxEnd = j;
            }
            bracketNum--;
            break;
          default:
            break;
        }
        if (j === scriptFinal.length - 1) {
          break;
        }
        j++;
      }

      //--------------------------------------------------------------------------
      let funcFinal = scriptFinal.slice(idxNameEnd, idxEnd);
      const deferredAssignments: Array<{ token: string; value: string }> = [];
      // Временно заменяет левую часть assignment токеном, чтобы чтение .value не ломало замену записи.
      const createDeferredAssignment = (value: string) => {
        const token = `__DF_CLIENT_SCRIPT_ASSIGN_${i}_${deferredAssignments.length}__`;
        deferredAssignments.push({ token, value });
        return token;
      };
      //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      //--------------------------------------------------------------------------
      // replace table length
      //--------------------------------------------------------------------------
      const tableLength = funcFinal.match(REGEX_TABLE_LENGTH);
      if (tableLength) {
        for (let k = 0; k < tableLength.length; k++) {
          while (funcFinal.indexOf(tableLength[k]) !== -1) {
            funcFinal = funcFinal.replace(tableLength[k], 'updObj.updParams.details.length');
          }
        }
      }

      //--------------------------------------------------------------------------
      // replace table fields
      //--------------------------------------------------------------------------
      const tableFieldsLefthand = funcFinal.match(REGEX_TABLE_I_FIELD_LEFTHAND);
      const tableFieldsNamesLefthand = funcFinal.match(REGEX_TABLE_I_FIELD_NAME_LEFTHAND);

      if (tableFieldsLefthand && tableFieldsNamesLefthand) {
        for (let k = 0; k < tableFieldsLefthand.length; k++) {
          while (funcFinal.indexOf(tableFieldsLefthand[k]) !== -1) {
            const fieldKey = getTableFieldKey(tableFieldsNamesLefthand[k]);
            const assignmentTarget =
              tableFieldsTypesMap.get(fieldKey) === FieldType.DICTIONARY
                ? `updObj.updParams.details${tableFieldsNamesLefthand[k]}.value=`
                : `updObj.updParams.details${tableFieldsNamesLefthand[k]}=`;
            funcFinal = funcFinal.replace(
              tableFieldsLefthand[k],
              createDeferredAssignment(assignmentTarget)
            );
          }
        }
      }

      const tableFieldsKeyLefthand = funcFinal.match(REGEX_TABLE_I_FIELD_KEY_LEFTHAND);
      const tableFieldsNamesKeyLefthand = funcFinal.match(REGEX_TABLE_I_FIELD_NAME_KEY_LEFTHAND);

      if (tableFieldsKeyLefthand && tableFieldsNamesKeyLefthand) {
        for (let k = 0; k < tableFieldsKeyLefthand.length; k++) {
          while (funcFinal.indexOf(tableFieldsKeyLefthand[k]) !== -1) {
            funcFinal = funcFinal.replace(
              tableFieldsKeyLefthand[k],
              createDeferredAssignment(
                `updObj.updParams.details${tableFieldsNamesKeyLefthand[k]}.key=`
              )
            );
          }
        }
      }

      const tableFields = funcFinal.match(REGEX_TABLE_I_FIELD);
      const tableFieldsNames = funcFinal.match(REGEX_TABLE_I_FIELD_NAME);
      if (tableFields && tableFieldsNames) {
        for (let k = 0; k < tableFields.length; k++) {
          while (funcFinal.indexOf(tableFields[k]) !== -1) {
            const tableFieldName = tableFieldsNames[k].split('.');
            const fieldType = tableFieldsTypesMap.get(tableFieldName[1]);
            funcFinal = funcFinal.replace(
              tableFields[k],
              getClientScriptValueExpression(
                `updObj.updParams.details${tableFieldsNames[k]}`,
                fieldType
              ),
            );
          }
        }
      }

      const tableFieldsKey = funcFinal.match(REGEX_TABLE_I_FIELD_KEY);
      const tableFieldsNamesKey = funcFinal.match(REGEX_TABLE_I_FIELD_NAME_KEY);
      if (tableFieldsKey && tableFieldsNamesKey) {
        for (let k = 0; k < tableFieldsKey.length; k++) {
          while (funcFinal.indexOf(tableFieldsKey[k]) !== -1) {
            funcFinal = funcFinal.replace(tableFieldsKey[k], `(updObj.updParams.details${tableFieldsNamesKey[k]}?.key ?? '')`);
          }
        }
      }
      //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

      //--------------------------------------------------------------------------
      // replace common fields
      //--------------------------------------------------------------------------
      const commonFieldsLeftHand = funcFinal.match(REGEX_COMMON_FIELD_LEFTHAND);
      const commonFieldsNamesLeftHand = funcFinal.match(REGEX_COMMON_FIELD_NAME_LEFTHAND);
      if (commonFieldsLeftHand && commonFieldsNamesLeftHand) {
        for (let k = 0; k < commonFieldsLeftHand.length; k++) {
          while (funcFinal.indexOf(commonFieldsLeftHand[k]) !== -1) {
            const field = fieldsTypes[commonFieldsNamesLeftHand[k]];
            const assignmentTarget =
              field?.type === FieldType.DICTIONARY
                ? field.isArray
                  ? `updObj.updParams.fields.${commonFieldsNamesLeftHand[k]}=`
                  : `updObj.updParams.fields.${commonFieldsNamesLeftHand[k]}.value=`
                : `updObj.updParams.fields.${commonFieldsNamesLeftHand[k]}=`;
            funcFinal = funcFinal.replace(
              commonFieldsLeftHand[k],
              createDeferredAssignment(assignmentTarget)
            );
          }
        }
      }

      const commonFieldsKeyLeftHand = funcFinal.match(REGEX_COMMON_FIELD_KEY_LEFTHAND);
      const commonFieldsNamesKeyLeftHand = funcFinal.match(REGEX_COMMON_FIELD_KEY_NAME_LEFTHAND);
      if (commonFieldsKeyLeftHand && commonFieldsNamesKeyLeftHand) {
        for (let k = 0; k < commonFieldsKeyLeftHand.length; k++) {
          while (funcFinal.indexOf(commonFieldsKeyLeftHand[k]) !== -1) {
            const field = fieldsTypes[commonFieldsNamesKeyLeftHand[k]];
            const assignmentTarget =
              field?.type === FieldType.DICTIONARY && field.isArray
                ? `updObj.updParams.fields.${commonFieldsNamesKeyLeftHand[k]}=`
                : `updObj.updParams.fields.${commonFieldsNamesKeyLeftHand[k]}.key=`;
            funcFinal = funcFinal.replace(
              commonFieldsKeyLeftHand[k],
              createDeferredAssignment(assignmentTarget)
            );
          }
        }
      }

      const commonField = funcFinal.match(REGEX_COMMON_FIELD);
      const commonFieldsNames = funcFinal.match(REGEX_COMMON_FIELD_NAME);
      if (commonField && commonFieldsNames) {
        for (let k = 0; k < commonField.length; k++) {
          const field = fieldsTypes[commonFieldsNames[k]];
          const newValueK = getClientScriptValueExpression(
            `updObj.updParams.fields.${commonFieldsNames[k]}`,
            field?.type,
            field?.isArray
          );

          while (funcFinal.indexOf(commonField[k]) !== -1) {
            funcFinal = funcFinal.replace(commonField[k], newValueK);
          }
        }
      }

      const commonFieldKey = funcFinal.match(REGEX_COMMON_FIELD_KEY);
      const commonFieldsNamesKey = funcFinal.match(REGEX_COMMON_FIELD_KEY_NAME);
      if (commonFieldKey && commonFieldsNamesKey) {
        for (let k = 0; k < commonFieldKey.length; k++) {
          while (funcFinal.indexOf(commonFieldKey[k]) !== -1) {
            const field = fieldsTypes[commonFieldsNamesKey[k]];
            const replacement =
              field?.type === FieldType.DICTIONARY && field.isArray
                ? `createDictionaryArrayValue(updObj.updParams.fields.${commonFieldsNamesKey[k]}).map(({ key }) => key).filter((key) => !!key)`
                : `(updObj.updParams.fields.${commonFieldsNamesKey[k]}?.key ?? '')`;
            funcFinal = funcFinal.replace(commonFieldKey[k], replacement);
          }
        }
      }

      deferredAssignments.forEach(({ token, value }) => {
        while (funcFinal.indexOf(token) !== -1) {
          funcFinal = funcFinal.replace(token, value);
        }
      });
      //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

      //--------------------------------------------------------------------------
      // update field value
      //--------------------------------------------------------------------------
      const tableLefthandMatches = [...(tableFieldsLefthand ?? []), ...(tableFieldsKeyLefthand ?? [])];

      if (tableLefthandMatches.length) {
        for (let n = 0; n < tableLefthandMatches.length; n++) {
          const name = tableLefthandMatches[n].match(/\.(\w+)\./);
          if (name) {
            updObj.updNames.details.push(name[1]);
          }
        }
      }

      const commonLefthandMatches = [...(commonFieldsLeftHand ?? []), ...(commonFieldsKeyLeftHand ?? [])];

      if (commonLefthandMatches.length) {
        for (let n = 0; n < commonLefthandMatches.length; n++) {
          const name = commonLefthandMatches[n].match(/(\b\w+\b)/);
          if (name && !updObj.updNames.fields.includes(name[0])) {
            updObj.updNames.fields.push(name[0]);
          }
        }
      }
      //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

      funcObj[funcName] = funcFinal.trim();
    }
  }

  const onChangeLines = scriptFinal.match(REGEX_ONCHANGE_LINE);
  if (onChangeLines) {
    for (let i = 0; i < onChangeLines.length; i++) {
      const name = onChangeLines[i].match(REGEX_ONCHANGE_NAME);
      const other = onChangeLines[i].match(REGEX_ONCHANGE_OTHER);
      if (name && other) {
        const func = other[0].replace(REGEX_SPACE_EQ_SEMICOLON, '');
        if (scriptNewParams.fields[name[0]] !== undefined) {
          if (
            isClientScriptValueChanged(
              scriptNewParams.fields[name[0]],
              scriptOldParams.fields[name[0]],
              fieldsTypes[name[0]]?.type,
              fieldsTypes[name[0]]?.isArray
            )
          ) {
            eval(`"use strict"; ${funcObj[func]}`);
          }
        }

        if (scriptNewParams.details.length !== 0) {
          if (scriptNewParams.details[0][name[0]] !== undefined) {
            for (let i = 0; i < scriptNewParams.details.length; i++) {
              if (
                (scriptOldParams.details[i] &&
                  isClientScriptValueChanged(
                    scriptNewParams.details[i][name[0]],
                    scriptOldParams.details[i][name[0]],
                    tableFieldsTypesMap.get(name[0])
                  )) ||
                scriptNewParams.details.length !== scriptOldParams.details.length
              ) {
                eval(`"use strict"; ${funcObj[func]}`);
              }
            }
          }
          // else {
          //   // // logMe(4,"no",newParams.details[0][name[0]] && newParams.details[0][name[0]])
          // }
        }
      }
    }
  }

  updObj.updParams = prepareClientScriptResult(
    updObj.updParams,
    fieldsTypes,
    tableFields,
    userTimeZone
  );

  return updObj;
};
